import React, { useRef } from 'react'
import { RedtailWorkflow } from '../../../../../../../models/Integration'
import { User } from '../../../../../../../models/User'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { Controller, useForm } from 'react-hook-form'
import { useUpdateIntegrationEntityMutation } from '../../../../../../../store/integrations/integrations'
import { useOutsideClick } from '../../../../../../../common/useOutsideClick'
import { useOverlayContainer } from '../../../../../../../common/useOverlayContainer'
import { Form, Overlay } from 'react-bootstrap'
import { SelectDropdown } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../../../components/Button/Button'

interface CompleteRedtailWorkflowStepFormValues {
  workflowOutcomeId?: string | null
  stepNotes?: string
}

interface CompleteRedtailWorkflowStepOverlayProps {
  targetRef: React.RefObject<HTMLElement | null>
  show: boolean
  onHide: () => void
  onWorkflowStepCompleted?: (workflow: RedtailWorkflow) => void
  workflow: RedtailWorkflow
  contact: User
  serviceId: string
}

export const CompleteRedtailWorkflowStepOverlay: React.FC<CompleteRedtailWorkflowStepOverlayProps> = ({
  targetRef,
  show,
  onHide,
  workflow,
  onWorkflowStepCompleted,
  contact,
  serviceId,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()

  const defaultValues = { workflowOutcomeId: null }
  const { control, handleSubmit, reset } = useForm<CompleteRedtailWorkflowStepFormValues>({ defaultValues })
  const overlayRef = useRef(null)
  const overlayContainer = useOverlayContainer(targetRef)

  const [triggerUpdateIntegrationEntity, { isLoading: isCreatingWorkflow }] = useUpdateIntegrationEntityMutation()

  const outcomeOptions =
    workflow.activeStep?.workflowStepOutcomes?.map((outcome) => {
      return { id: `${outcome.id}`, label: outcome.name ?? `Outcome id: ${outcome.id}` }
    }) ?? []

  useOutsideClick(overlayRef, () => {
    onHide()
  })

  const handleCancel = () => {
    reset()
    onHide()
  }

  const _handleWorkflowStepCompleted = async (formValues: CompleteRedtailWorkflowStepFormValues) => {
    try {
      const { data: updatedWorkflow } = await triggerUpdateIntegrationEntity({
        accountId,
        relatedTo: { id: '' },
        connector: serviceId,
        entityType: 'workflow_steps',
        entityId: `${workflow.id}-${workflow.activeStep?.id}`,
        payload: { outcome_id: formValues.workflowOutcomeId, step_notes: formValues.stepNotes },
      }).unwrap()
      reset()
      onWorkflowStepCompleted?.({
        id: updatedWorkflow.id,
        activeStep: updatedWorkflow.activeStep,
        name: updatedWorkflow.name,
        description: updatedWorkflow.description,
        label: updatedWorkflow.label,
      } as RedtailWorkflow)
    } catch {
      // Failed API calls are tracked centrally; nothing else to report here
    }
  }

  const handleWorkflowStepCompleted = tracked(
    _handleWorkflowStepCompleted,
    {
      eventName: TrackEvent.UPDATE_WORKFLOW_STEP_IN_CRM,
    },
    {
      eventDetailsExtractor: (formValues: CompleteRedtailWorkflowStepFormValues) => {
        return {
          connector: serviceId,
          contactId: contact.id,
          workflowOutcomeId: formValues.workflowOutcomeId,
        }
      },
    },
  )

  return (
    <Overlay ref={overlayRef} target={targetRef} container={overlayContainer} show={show} placement='left'>
      {({ ref, style }) => (
        <div ref={ref} style={style} className='start-workflow-overlay d-flex flex-column gap-3 border-default zocks-border-0.25 p-4'>
          <h4>Complete {workflow.activeStep?.name}</h4>
          <Controller
            name='workflowOutcomeId'
            control={control}
            rules={{ required: 'Outcome is required' }}
            render={({ field, fieldState }) => (
              <div>
                <Form.Label>Select outcome</Form.Label>
                <SelectDropdown
                  initialSelectedOption={null}
                  options={outcomeOptions}
                  className='py-2 zocks-px-0.75'
                  onOptionSelected={(option) => field.onChange(option.id)}
                  placeholder={`Select outcome`}
                />
                {fieldState.error ? <FormFeedback>{fieldState.error.message}</FormFeedback> : null}
              </div>
            )}
          />
          <Controller
            name='stepNotes'
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <Form.Label>Step Note</Form.Label>
                <Form.Control id={field.name} {...field} />
                {fieldState.error ? <FormFeedback>{fieldState.error.message}</FormFeedback> : null}
              </div>
            )}
          />
          <div className='align-self-end d-flex gap-2'>
            <Button variant='secondary' className='button-small' onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant='primary' className='button-small' onClick={handleSubmit(handleWorkflowStepCompleted)} loading={isCreatingWorkflow}>
              <i className='bi bi-check' />
              Complete
            </Button>
          </div>
        </div>
      )}
    </Overlay>
  )
}
